'use client'

import { motion } from 'motion/react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { SectionLabel } from './player-card'
import { useMk8Player } from '@/hooks/use-mk8-player'

const PREFIX_LABELS: Record<string, string> = {
  Gu: 'MK8',
  Du: 'DLC',
  G3ds: '3DS',
  Gwii: 'Wii',
  G64: 'N64',
  Gds: 'DS',
  Ggc: 'GCN',
  Gagb: 'GBA',
  Gsfc: 'SNES',
  Dgc: 'GCN',
  Dwii: 'Wii',
  Dsfc: 'SNES',
  Dagb: 'GBA',
  D3ds: '3DS',
  B3ds: '3DS',
  Bgc: 'GCN',
  Bsfc: 'SNES',
  Bu: 'Battle',
}

function humanizeToken(s = '') {
  return s
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeCourseName(name: string) {
  if (!name) return 'Unknown Track'
  const m = /^([A-Za-z0-9]+)_(.+)$/.exec(name)
  if (!m) return humanizeToken(name)
  const prefix = m[1]
  const core = humanizeToken(m[2])
  const label = PREFIX_LABELS[prefix] || prefix
  return `${label} ${core}`
}

function mapAsset(name: string) {
  return `/assets/maps/${encodeURIComponent(String(name || '').trim())}.png`
}

const MMR_HISTORY = [
  { date: 'Jan 1', mmr: 6000 },
  { date: 'Jan 8', mmr: 6280 },
  { date: 'Jan 15', mmr: 6140 },
  { date: 'Jan 22', mmr: 6520 },
  { date: 'Feb 1', mmr: 6800 },
  { date: 'Feb 8', mmr: 7050 },
  { date: 'Feb 15', mmr: 6920 },
  { date: 'Feb 22', mmr: 7350 },
  { date: 'Mar 1', mmr: 7600 },
  { date: 'Mar 8', mmr: 7440 },
  { date: 'Mar 15', mmr: 7820 },
  { date: 'Mar 22', mmr: 8010 },
  { date: 'Apr 1', mmr: 8240 },
  { date: 'Apr 8', mmr: 8100 },
  { date: 'Apr 15', mmr: 8420 },
]

const RECENT_RACES = [
  { track: 'Rainbow Road', pos: 1, mmrChange: +28, time: '2:14.331', driver: 'Unknown driver', body: 'Unknown kart' },
  { track: 'Mushroom Gorge', pos: 2, mmrChange: +14, time: '1:52.882', driver: 'Unknown driver', body: 'Unknown kart' },
  { track: 'Mount Wario', pos: 1, mmrChange: +28, time: '2:44.111', driver: 'Unknown driver', body: 'Unknown kart' },
  { track: 'Toad Harbor', pos: 3, mmrChange: +6, time: '2:03.445', driver: 'Unknown driver', body: 'Unknown kart' },
  { track: 'Electrodrome', pos: 1, mmrChange: +28, time: '1:57.009', driver: 'Unknown driver', body: 'Unknown kart' },
  { track: 'Cloudtop Cruise', pos: 4, mmrChange: -8, time: '2:21.774', driver: 'Unknown driver', body: 'Unknown kart' },
  { track: 'Big Blue', pos: 1, mmrChange: +28, time: '2:08.559', driver: 'Unknown driver', body: 'Unknown kart' },
]

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ value: number; payload?: any }>
}) {
  if (active && payload && payload.length) {
    return (
      <div
        className="px-3 py-2 rounded-lg"
        style={{
          background: 'rgba(8,8,14,0.95)',
          border: '1px solid rgba(0,234,255,0.2)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <p
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '10px',
            letterSpacing: '0.15em',
            color: 'rgba(255,255,255,0.4)',
            fontWeight: 600,
          }}
        >
Race {payload?.[0]?.payload?.raceIndex || '-'}
        </p>
        <p
          style={{
            fontFamily: "'Russo One', sans-serif",
            fontSize: '20px',
            color: '#00eaff',
          }}
        >
          {payload[0].value.toLocaleString()}
        </p>
      </div>
    )
  }
  return null
}

export function RaceChart({ profileId }: { profileId?: string }) {
  const { myHistory, myRecentRaces, summary } = useMk8Player(profileId)

  const history = myHistory && myHistory.length > 0 ? myHistory : MMR_HISTORY
  const races = myRecentRaces && myRecentRaces.length ? myRecentRaces : RECENT_RACES

  const last20 = history.slice(-20)
  const plotHistory = last20.map((h: any, i: number) => ({
    ...h,
    xKey: `Race ${i + 1}`,
    raceIndex: i + 1,
  }))

  const first = history[0]?.mmr || 0
  const last = history[history.length - 1]?.mmr || 0
  const delta = last - first
  const pct = first > 0 ? ((delta / first) * 100).toFixed(1) : '0.0'

  const seasonLabel = summary?.currentSeason?.name
    ? `${summary.currentSeason.name} Progress`
    : 'Current Season Progress'

  const mmrValues = plotHistory.map((p: any) => Number(p.mmr || 0))
  const minMmr = mmrValues.length ? Math.min(...mmrValues) : 0
  const maxMmr = mmrValues.length ? Math.max(...mmrValues) : 100
  const yPad = minMmr === maxMmr ? Math.max(60, Math.floor(maxMmr * 0.03)) : 120

  return (
    <section
      id="leaderboard"
      className="relative py-24 px-6"
      style={{ background: '#08080e' }}
    >
      <div
        className="absolute top-0 left-6 right-6 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(0,234,255,0.15), transparent)',
        }}
      />

      <div className="max-w-5xl mx-auto">
        <SectionLabel label="MMR HISTORY" />

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl p-6 mb-8"
          style={{
            background: 'rgba(0,234,255,0.03)',
            border: '1px solid rgba(0,234,255,0.1)',
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <p
                style={{
                  fontFamily: "'Russo One', sans-serif",
                  color: '#fff',
                  fontSize: '20px',
                }}
              >
                {seasonLabel}
              </p>
              <p
                style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.35)',
                }}
              >
                {delta >= 0 ? '+' : ''}{delta.toLocaleString()} MMR over the last {last20.length} races
              </p>
            </div>
            <div
              className="px-3 py-1 rounded-full"
              style={{
                background: 'rgba(0,255,136,0.1)',
                border: '1px solid rgba(0,255,136,0.2)',
              }}
            >
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: '12px',
                  color: '#00ff88',
                  letterSpacing: '0.1em',
                }}
              >
                {delta >= 0 ? '↑' : '↓'} {delta >= 0 ? '+' : ''}{pct}%
              </span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <AreaChart
              data={plotHistory}
              margin={{ top: 5, right: 5, bottom: 0, left: -10 }}
            >
              <defs>
                <linearGradient id="mmrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00eaff" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00eaff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.04)"
              />
              <XAxis
                dataKey="xKey"
                tick={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: 10,
                  fill: 'rgba(255,255,255,0.3)',
                  fontWeight: 600,
                }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[
                  Math.max(0, minMmr - yPad),
                  maxMmr + yPad,
                ]}
                tick={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: 10,
                  fill: 'rgba(255,255,255,0.3)',
                  fontWeight: 600,
                }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="mmr"
                stroke="#00eaff"
                strokeWidth={3}
                fill="url(#mmrGrad)"
                dot={{ r: 2, fill: '#00eaff', stroke: 'transparent' }}
                activeDot={{ r: 6, fill: '#00eaff', stroke: '#08080e', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Recent Races */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div
            className="px-6 py-4"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <p
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                letterSpacing: '0.2em',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '11px',
              }}
            >
              RECENT RACES
            </p>
          </div>

          {races.map((race, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-white/[0.02]"
              style={{
                borderBottom:
                  i < races.length - 1
                    ? '1px solid rgba(255,255,255,0.04)'
                    : 'none',
              }}
            >
              {/* Position */}
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-bold"
                style={{
                  background:
                    race.pos === 1
                      ? 'rgba(255,230,0,0.15)'
                      : race.pos <= 3
                        ? 'rgba(0,234,255,0.1)'
                        : 'rgba(255,255,255,0.05)',
                  color:
                    race.pos === 1
                      ? '#ffe600'
                      : race.pos <= 3
                        ? '#00eaff'
                        : 'rgba(255,255,255,0.4)',
                  fontFamily: "'Russo One', sans-serif",
                  fontSize: '14px',
                }}
              >
                {race.pos}
              </div>

              {/* Track name */}
              <div className="flex-1 flex items-center gap-3">
                <img
                  src={mapAsset(race.track)}
                  alt=""
                  aria-hidden="true"
                  onError={(e: any) => {
                    e.currentTarget.onerror = null
                    e.currentTarget.src = '/assets/maps/fallback.svg'
                  }}
                  style={{
                    width: 56,
                    height: 32,
                    borderRadius: 6,
                    objectFit: 'cover',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                />
                <div>
                  <p
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      fontSize: '15px',
                      color: '#fff',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {normalizeCourseName(race.track)}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.55)',
                    }}
                  >
                    {String(race.driver || 'Unknown driver')} · {String(race.body || 'Unknown kart')}
                  </p>
                </div>
              </div>

              {/* MMR change */}
              <div
                className="px-3 py-1 rounded-lg"
                style={{
                  background:
                    race.mmrChange > 0
                      ? 'rgba(0,255,136,0.08)'
                      : 'rgba(255,53,68,0.08)',
                  border: `1px solid ${race.mmrChange > 0 ? 'rgba(0,255,136,0.2)' : 'rgba(255,53,68,0.2)'}`,
                }}
              >
                <span
                  style={{
                    fontFamily: "'Russo One', sans-serif",
                    fontSize: '14px',
                    color: race.mmrChange > 0 ? '#00ff88' : '#ff3544',
                  }}
                >
                  {race.mmrChange > 0 ? '+' : ''}
                  {race.mmrChange}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
